/* eslint-disable @typescript-eslint/no-non-null-assertion */
import type { PackageReference, TriggerResource } from "@octopusdeploy/octopus-server-client";
import { Fragment, useMemo } from "react";
import { AvailableFeedTriggerCallout, MultipleRelatedFeedTriggersCallout, SingleRelatedFeedTriggerCallout } from "./RelatedFeedTriggerCallouts";
import { getRelatedFeedTriggersForReferencedPackages, getPackageNamesUsedInFeedTriggers } from "./triggerPackageRelations";
export interface RelatedTriggersDependencies {
    spaceId: string;
    projectSlug: string;
    projectTriggers: TriggerResource[];
    deploymentActionSlug: string;
}
interface CalloutReferencedPackageFeedTriggerConnectionsProps {
    dependencies: RelatedTriggersDependencies;
    packageReferences: PackageReference[];
    displayAvailableTriggerCallout?: boolean;
}
export function CalloutReferencedPackageFeedTriggerConnections({ dependencies, packageReferences, displayAvailableTriggerCallout }: CalloutReferencedPackageFeedTriggerConnectionsProps) {
    const { spaceId, projectSlug } = dependencies;
    const relatedTriggers = useMemo(() => getRelatedFeedTriggersForReferencedPackages(dependencies, packageReferences), [dependencies, packageReferences]);
    const packageNames = getPackageNamesUsedInFeedTriggers(relatedTriggers, packageReferences);
    // We only want to display the available trigger callout when it applies
    // to the enabled package selector feeds and when it won't clutter the UI.
    // In practice this normally means when the main package selection for a
    // step is a list of packages.
    if (relatedTriggers.length === 0 && displayAvailableTriggerCallout) {
        return <AvailableFeedTriggerCallout spaceId={spaceId} projectSlug={projectSlug}/>;
    }
    // No package name means it's a primary package which are handled separately
    if (packageNames.length === 0) {
        return <></>;
    }
    const uniqueTriggerCount = [...new Set(relatedTriggers.map((trigger) => trigger.Id))].length;
    if (uniqueTriggerCount === 1) {
        const triggerId = relatedTriggers[0].Id;
        if (packageNames.length === 1) {
            return <SingleRelatedFeedTriggerCallout triggerId={triggerId} text={<>The package {strongPackageName(packageNames[0])} is connected to an external feed trigger.</>} spaceId={spaceId} projectSlug={projectSlug}/>;
        }
        else {
            return <SingleRelatedFeedTriggerCallout triggerId={triggerId} text={<>The {createPackageNameSentence(packageNames)} packages are connected to an external feed trigger.</>} spaceId={spaceId} projectSlug={projectSlug}/>;
        }
    }
    else {
        if (packageNames.length === 1) {
            return <MultipleRelatedFeedTriggersCallout text={<>The package {strongPackageName(packageNames[0])} is connected to external feed triggers.</>} spaceId={spaceId} projectSlug={projectSlug}/>;
        }
        else {
            return <MultipleRelatedFeedTriggersCallout text={<>The {createPackageNameSentence(packageNames)} packages are connected to external feed triggers.</>} spaceId={spaceId} projectSlug={projectSlug}/>;
        }
    }
}
const createPackageNameSentence = (packageNames: string[]) => {
    switch (packageNames.length) {
        case 0:
            return <></>;
        case 1:
            return <>{strongPackageName(packageNames[0])}</>;
        case 2:
            return (<>
                    {strongPackageName(packageNames[0])} and {strongPackageName(packageNames[1])}
                </>);
        default:
            const lastPackageName = packageNames.pop();
            const secondLastPackageName = packageNames.pop();
            return (<>
                    {packageNames.map((name) => strongPackageName(name, true))} {strongPackageName(secondLastPackageName!)} and {strongPackageName(lastPackageName!)}
                </>);
    }
};
const strongPackageName = (name: string, withComma: boolean = false) => {
    return (<Fragment key={name}>
            <strong>{name}</strong>
            {withComma && <>,</>}
        </Fragment>);
};
